import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../../components/Modal';
import { Flex, Text } from 'rebass';
import gaugeColumns from './Gauges.columns';
import credentialColumns from './Credentials.columns';
import DataTable from '../Table/DataTable';
import { Label } from '../Form';
import { Credential, Gauge, TesterVendor } from './types';
import { ModalHeader } from '../Modal';
import StatusChip from '../Status/StatusChip';
import { StatusStyle } from '../Status/StatusDot';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import useNotify from '../../hooks/useNotify';
import { useMutation } from '@apollo/client';
import { ApolloError } from '@apollo/client/errors';
import FilePreview from '../File/FilePreview';
import { gql } from '@/modes/waterSupplier/gql';
import RequirePerms from '@/components/RequirePerms';

const SET_TESTER_APPROVAL = gql(`
  mutation SetTesterApproval(
    $testerProfileId: ID!
    $approval: SubmissionApproval
  ) {
    setTesterApproval(testerProfileId: $testerProfileId, approval: $approval) {
      id
      approval
    }
  }
`);

type TesterModalProps = {
  onClose: () => void;
  tester: TesterVendor;
};

const TesterModal: React.FC<TesterModalProps> = ({ onClose, tester }) => {
  const { credentials, backflowTester, firstName, lastName, approval, id } =
    tester.profile;

  const gauges = backflowTester?.gauges ?? [];

  const { showError } = useNotify();

  const [setTesterApproval] = useMutation<
    string,
    { testerProfileId: string; approval: string }
  >(SET_TESTER_APPROVAL, {
    // onCompleted: advanceAndReload,
    onError: useCallback((error: ApolloError) => {
      showError('Error updating tester approval', error);
    }, []),
  });

  const [gaugeIds] = useState(gauges ? gauges.map((a) => a.id) : []);

  const [credentialIds] = useState(
    credentials ? credentials.map((a) => a.id) : []
  );

  const imageGauges = gauges.filter((gauge) => gauge.image);
  const imageCredentials = credentials.filter((credential) => credential.image);

  const [selectedRow, setSelectedRow] = useState<Credential | Gauge>(
    imageGauges[0]
  );

  let approvalStatus: StatusStyle = 'default';
  switch (approval) {
    case 'PENDING':
      approvalStatus = 'default';
      break;
    case 'APPROVED':
      approvalStatus = 'success';
      break;
    case 'REJECTED':
    case 'SUSPENDED':
      approvalStatus = 'error';
      break;
  }

  return (
    <Modal onClose={onClose}>
      <ModalHeader
        alignItems="center"
        title={tester && `${firstName} ${lastName}`}
      >
        <StatusChip ml={2} status={approvalStatus}>
          {constantToLabel(approval)}
        </StatusChip>
      </ModalHeader>
      <ModalBody>
        <Flex flexDirection="row">
          <Flex flexGrow={1} p={3} flexDirection="column">
            <Label pb="3">Gauges</Label>
            {gauges && gauges.length ? (
              <DataTable
                items={gauges}
                columns={gaugeColumns}
                itemIds={gaugeIds}
                activeIds={[selectedRow?.id]}
                onRowClick={(row: Gauge) => {
                  setSelectedRow(row);
                }}
              />
            ) : (
              <Text fontSize={1}>This profile has no gauges.</Text>
            )}
            <Label py="3">Credentials</Label>
            {credentials && credentials.length ? (
              <DataTable
                items={credentials}
                columns={credentialColumns}
                itemIds={credentialIds}
                activeIds={[selectedRow?.id]}
                onRowClick={(row: Credential) => {
                  setSelectedRow(row);
                }}
              />
            ) : (
              <Text fontSize={1}>This profile has no credentials.</Text>
            )}
          </Flex>
          {selectedRow?.image && (
            <FilePreview
              ml={1}
              mr={3}
              my={3}
              maxWidth="45%"
              alignSelf="center"
              size={240}
              file={selectedRow.image}
            />
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <RequirePerms vendors="UPDATE">
          <Flex flexGrow={1} sx={{ gap: 3 }}>
            {approval !== 'APPROVED' && (
              <Button
                variant="success"
                icon="check-circle"
                onClick={() =>
                  setTesterApproval({
                    variables: {
                      testerProfileId: id,
                      approval: 'APPROVED',
                    },
                  })
                }
              >
                Approve
              </Button>
            )}
            {['PENDING', 'APPROVED'].includes(approval) && (
              <Button
                variant="danger"
                icon="slash-circle"
                onClick={() =>
                  setTesterApproval({
                    variables: {
                      testerProfileId: id,
                      approval: 'REJECTED',
                    },
                  })
                }
              >
                Reject
              </Button>
            )}
            {approval === 'APPROVED' && (
              <Button
                variant="danger"
                icon="slash-circle"
                onClick={() =>
                  setTesterApproval({
                    variables: {
                      testerProfileId: id,
                      approval: 'SUSPENDED',
                    },
                  })
                }
              >
                Suspend
              </Button>
            )}
          </Flex>
        </RequirePerms>
        <Button
          flexGrow={0}
          flexShrink={0}
          variant="outline"
          icon="slash-circle"
          onClick={onClose}
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

TesterModal.propTypes = {
  onClose: PropTypes.any,
  tester: PropTypes.any,
};

export default TesterModal;
